import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiSearch, FiRefreshCw, FiChevronLeft, FiChevronRight, FiCalendar,
  FiZap, FiGitMerge, FiUserCheck, FiInfo, FiX, FiChevronUp, FiChevronDown,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import {
  getCarrierDownloadSessionLogs, type SessionLogRecord,
  getCarrierDownloadMergePreview, type MergePreviewRecord,
} from '../../services/carrierDownloadService';
import CarrierDownloadRules from './CarrierDownloadRules';
import { useCarrierSession } from '../../context/CarrierSessionContext';
import { useCarrierDownloadStats } from '../../context/CarrierDownloadStatsContext';

// Local YYYY-MM-DD — matches the date inputs and the presets below. (toISOString is
// UTC, so in US evenings "today" used to come out as tomorrow.)
function localYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function todayStr(): string {
  return localYMD(new Date());
}
function weekAgoStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return localYMD(d);
}
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return '—'; }
}

function formatMergeDate(value: string | null | undefined): string {
  if (!value) return '—';
  const datePart = value.split(' ')[0];
  const [y, m, d] = datePart.split('-');
  return (y && m && d) ? `${m}/${d}/${y}` : '—';
}

function cleanTransactionString(txString: string): string {
  if (!txString) return '—';
  // Remove tags: nbs, rwl, pch (case-insensitive, with word boundaries)
  return txString.replace(/\s*(nbs|rwl|pch)\s*/gi, ' ').replace(/\s+/g, ' ').trim();
}

function mergeStatusMeta(raw: string | null | undefined): { label: string; tone: 'consumed' | 'not-consumed' | 'partial' } {
  const value = (raw ?? '').trim().toLowerCase();

  if (!value) return { label: 'Not Merged', tone: 'not-consumed' };
  if (value === 'success') return { label: 'Merged', tone: 'consumed' };
  if (value === '2pass disabled' || value === '2pass_disabled' || value === 'pass2 disabled' || value === 'pass2_disabled') {
    return { label: 'Pass2 Disabled', tone: 'partial' };
  }

  if (value === 'merged') return { label: 'Merged', tone: 'consumed' };
  if (value === 'not merged' || value === 'not_merged' || value === 'failed' || value === 'error') {
    return { label: 'Not Merged', tone: 'not-consumed' };
  }

  return { label: raw!.replace(/_/g, ' '), tone: 'consumed' };
}

// Renders insured/policy/carrier name as plain text when its _url is null, or as a link when present.
const LinkableCell: React.FC<{ value: string; url: string | null }> = ({ value, url }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className="cdsl-link">
      {value}
    </a>
  ) : (
    <>{value}</>
  );

// action_category comes back as one of 3 values: AI Merge, Direct Merge, or User Review.
// "ai" is checked before the generic "merge" match since "AI Merge" contains both words.
function actionCategoryMeta(category: string): { Icon: IconType; tone: string } {
  const c = category.toLowerCase();
  if (c.includes('ai')) return { Icon: FiZap, tone: 'ai-merge' };
  if (c.includes('direct') || c.includes('merge')) return { Icon: FiGitMerge, tone: 'direct-merge' };
  if (c.includes('user') || c.includes('review')) return { Icon: FiUserCheck, tone: 'user-review' };
  return { Icon: FiInfo, tone: 'default' };
}

const ITEMS_OPTIONS = [10, 20, 50];

function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

type TabKey = 'sessions' | 'mergePreview' | 'rules';

// ── AI Merge Preview: client-side filter + sort ──────────────────────────────
// carrier-download-session-rows only takes a date range + skip/top (no sort or
// filter params), so the tab loads every row for the range in batches and does
// filtering, sorting and paging in the browser. MP_MAX_ROWS caps a huge range.
const MP_BATCH = 500;
const MP_MAX_ROWS = 5000;

type MpSortKey = 'insured' | 'policy' | 'carrier' | 'lob' | 'effective' | 'transaction' | 'status' | 'action' | 'reason';
type MpSort = { key: MpSortKey; dir: 'asc' | 'desc' };

const MP_COLUMNS: [MpSortKey, string][] = [
  ['insured', 'Insured'], ['policy', 'Policy #'], ['carrier', 'Carrier'], ['lob', 'Line of Business'],
  ['effective', 'Effective / Expiration'], ['transaction', 'Transaction'], ['status', 'Merge Status'],
  ['action', 'Action'], ['reason', 'Reason'],
];

const mpActionLabel = (row: MergePreviewRecord) => (row.action_category || '').replace(/_/g, ' ').trim() || '—';
const mpTransaction = (row: MergePreviewRecord) => cleanTransactionString(row.transaction_string);
const mpStatus = (row: MergePreviewRecord) => mergeStatusMeta(row.merge_status).label;
const mpReason = (row: MergePreviewRecord) => (row.planned_action_reason || '').trim() || '—';

/** Sort value for a column — the same text the cell shows; effective date as YYYY-MM-DD. */
function mpSortValue(row: MergePreviewRecord, key: MpSortKey): string {
  switch (key) {
    case 'insured': return row.insured_name_ivans || '';
    case 'policy': return row.policy_number_ivans || '';
    case 'carrier': return row.carrier_name_ivans || '';
    case 'lob': return row.line_of_business || '';
    case 'effective': return (row.policy_effective_date || '').split(' ')[0];
    case 'transaction': return mpTransaction(row);
    case 'status': return mpStatus(row);
    case 'action': return mpActionLabel(row);
    case 'reason': return mpReason(row);
  }
}

/** Distinct, sorted values for a filter dropdown. */
function distinctValues(rows: MergePreviewRecord[], pick: (r: MergePreviewRecord) => string): string[] {
  return Array.from(new Set(rows.map(pick))).sort((a, b) => a.localeCompare(b));
}

const CarrierDownloadSessionLogs: React.FC = () => {
  const [activeTab, setActiveTab]     = useState<TabKey>('sessions');
  const [from, setFrom]               = useState(weekAgoStr());
  const [to, setTo]                   = useState(todayStr());
  const [logs, setLogs]               = useState<SessionLogRecord[]>([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(false);
  const [page, setPage]               = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchFrom, setSearchFrom]   = useState(weekAgoStr());
  const [searchTo, setSearchTo]       = useState(todayStr());

  const [mpFrom, setMpFrom]               = useState(weekAgoStr());
  const [mpTo, setMpTo]                   = useState(todayStr());
  // Every row for the selected date range (see MP_MAX_ROWS); filtered/sorted/paged below.
  const [mpAll, setMpAll]                 = useState<MergePreviewRecord[]>([]);
  const [mpServerTotal, setMpServerTotal] = useState(0);
  const [mpQuery, setMpQuery]             = useState('');
  const [mpTxFilter, setMpTxFilter]       = useState('');
  const [mpStatusFilter, setMpStatusFilter] = useState('');
  const [mpActionFilter, setMpActionFilter] = useState('');
  const [mpReasonFilter, setMpReasonFilter] = useState('');
  const [mpSort, setMpSort]               = useState<MpSort>({ key: 'effective', dir: 'desc' });
  const [mpLoading, setMpLoading]         = useState(false);
  const [mpPage, setMpPage]               = useState(1);
  const [mpItemsPerPage, setMpItemsPerPage] = useState(10);  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const dateFilterMenuRef = React.useRef<HTMLDivElement>(null);  const [mpSearchFrom, setMpSearchFrom]   = useState(weekAgoStr());
  const [mpSearchTo, setMpSearchTo]       = useState(todayStr());

  const { openSession, sessionPanel } = useCarrierSession();
  const { setStats: setCdStats } = useCarrierDownloadStats();

  // Each tab keeps only its latest request's result — changing dates quickly must
  // not let an older, slower response overwrite the newer list.
  const logsSeqRef = React.useRef(0);
  const mpSeqRef = React.useRef(0);

  const fetchLogs = useCallback((f: string, t: string, pg: number, perPage: number) => {
    const seq = ++logsSeqRef.current;
    setLoading(true);
    getCarrierDownloadSessionLogs(f, t, perPage, (pg - 1) * perPage)
      .then(res => { if (seq === logsSeqRef.current) { setLogs(res.records); setTotal(res.total); } })
      .catch(() => { if (seq === logsSeqRef.current) { setLogs([]); setTotal(0); } })
      .finally(() => { if (seq === logsSeqRef.current) setLoading(false); });
  }, []);

  useEffect(() => {
    fetchLogs(searchFrom, searchTo, page, itemsPerPage);
  }, [fetchLogs, searchFrom, searchTo, page, itemsPerPage]);

  // Loads every row for the range in MP_BATCH-sized pages (up to MP_MAX_ROWS).
  const fetchMergePreview = useCallback(async (f: string, t: string) => {
    const seq = ++mpSeqRef.current;
    setMpLoading(true);
    try {
      const rows: MergePreviewRecord[] = [];
      let serverTotal = 0;
      while (rows.length < MP_MAX_ROWS) {
        const res = await getCarrierDownloadMergePreview(f, t, MP_BATCH, rows.length);
        if (seq !== mpSeqRef.current) return;
        serverTotal = res.total;
        rows.push(...res.records);
        if (res.records.length < MP_BATCH || rows.length >= res.total) break;
      }
      setMpAll(rows.slice(0, MP_MAX_ROWS));
      setMpServerTotal(serverTotal);
    } catch {
      if (seq === mpSeqRef.current) { setMpAll([]); setMpServerTotal(0); }
    } finally {
      if (seq === mpSeqRef.current) setMpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'mergePreview') {
      void fetchMergePreview(mpSearchFrom, mpSearchTo);
    }
  }, [activeTab, fetchMergePreview, mpSearchFrom, mpSearchTo]);

  // Dropdown options come from the loaded rows, so they always match the data.
  const mpOptions = useMemo(() => ({
    transaction: distinctValues(mpAll, mpTransaction),
    status: distinctValues(mpAll, mpStatus),
    action: distinctValues(mpAll, mpActionLabel),
    reason: distinctValues(mpAll, mpReason),
  }), [mpAll]);

  const mpFiltered = useMemo(() => {
    const q = mpQuery.trim().toLowerCase();
    const rows = mpAll.filter(row =>
      (!mpTxFilter || mpTransaction(row) === mpTxFilter) &&
      (!mpStatusFilter || mpStatus(row) === mpStatusFilter) &&
      (!mpActionFilter || mpActionLabel(row) === mpActionFilter) &&
      (!mpReasonFilter || mpReason(row) === mpReasonFilter) &&
      (!q || [row.insured_name_ivans, row.policy_number_ivans, row.carrier_name_ivans, row.line_of_business, row.planned_action_reason]
        .some(v => (v || '').toLowerCase().includes(q))));
    const dir = mpSort.dir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const av = mpSortValue(a, mpSort.key);
      const bv = mpSortValue(b, mpSort.key);
      if (!av && bv) return 1;   // blanks last, both directions
      if (av && !bv) return -1;
      return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [mpAll, mpQuery, mpTxFilter, mpStatusFilter, mpActionFilter, mpReasonFilter, mpSort]);

  // Any filter/sort change goes back to page 1.
  useEffect(() => { setMpPage(1); }, [mpQuery, mpTxFilter, mpStatusFilter, mpActionFilter, mpReasonFilter, mpSort]);

  const mpTotal = mpFiltered.length;
  const mpLogs = useMemo(
    () => mpFiltered.slice((mpPage - 1) * mpItemsPerPage, mpPage * mpItemsPerPage),
    [mpFiltered, mpPage, mpItemsPerPage],
  );
  const mpHasFilters = !!(mpQuery || mpTxFilter || mpStatusFilter || mpActionFilter || mpReasonFilter);
  const clearMpFilters = () => {
    setMpQuery(''); setMpTxFilter(''); setMpStatusFilter(''); setMpActionFilter(''); setMpReasonFilter('');
  };
  const toggleMpSort = (key: MpSortKey) => {
    setMpSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'effective' ? 'desc' : 'asc' });
  };
  const mpFilterControls: { label: string; allLabel: string; value: string; set: (v: string) => void; options: string[] }[] = [
    { label: 'Transaction', allLabel: 'All Transactions', value: mpTxFilter, set: setMpTxFilter, options: mpOptions.transaction },
    { label: 'Merge Status', allLabel: 'All Statuses', value: mpStatusFilter, set: setMpStatusFilter, options: mpOptions.status },
    { label: 'Action', allLabel: 'All Actions', value: mpActionFilter, set: setMpActionFilter, options: mpOptions.action },
    { label: 'Reason', allLabel: 'All Reasons', value: mpReasonFilter, set: setMpReasonFilter, options: mpOptions.reason },
  ];

  // Close date filter popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dateFilterMenuRef.current && !dateFilterMenuRef.current.contains(e.target as Node)) {
        setDateFilterOpen(false);
      }
    };
    if (dateFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dateFilterOpen]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const mpTotalPages = Math.max(1, Math.ceil(mpTotal / mpItemsPerPage));

  // Custom From/To apply as soon as they change — there is no separate Search button.
  const handleFromChange = (value: string) => {
    if (activeTab === 'mergePreview') { setMpFrom(value); setMpSearchFrom(value); setMpPage(1); }
    else { setFrom(value); setSearchFrom(value); setPage(1); }
  };
  const handleToChange = (value: string) => {
    if (activeTab === 'mergePreview') { setMpTo(value); setMpSearchTo(value); setMpPage(1); }
    else { setTo(value); setSearchTo(value); setPage(1); }
  };

  const handleItemsPerPageChange = (val: number) => {
    setItemsPerPage(val);
    setPage(1);
  };

  const handleMpItemsPerPageChange = (val: number) => {
    setMpItemsPerPage(val);
    setMpPage(1);
  };

  // Date preset utilities
  const toYMD = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const applyDatePreset = (preset: "today" | "week" | "month") => {
    const now = new Date();
    const toDate = toYMD(now);
    const start = new Date(now);
    
    if (preset === "week") start.setDate(start.getDate() - 6);
    else if (preset === "month") start.setDate(start.getDate() - 29);
    
    const fromDate = toYMD(start);
    
    if (activeTab === "mergePreview") {
      setMpFrom(fromDate);
      setMpTo(toDate);
      setMpSearchFrom(fromDate);
      setMpSearchTo(toDate);
      setMpPage(1);
    } else {
      setFrom(fromDate);
      setTo(toDate);
      setSearchFrom(fromDate);
      setSearchTo(toDate);
      setPage(1);
    }
  };

  const getActiveDatePreset = (): "today" | "week" | "month" | null => {
    const fromDate = activeTab === "mergePreview" ? mpFrom : from;
    const toDate = activeTab === "mergePreview" ? mpTo : to;
    
    if (!fromDate || !toDate) return null;
    const now = new Date();
    const today = toYMD(now);
    if (toDate !== today) return null;
    if (fromDate === today) return "today";
    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - 6);
    if (fromDate === toYMD(weekStart)) return "week";
    const monthStart = new Date(now);
    monthStart.setDate(monthStart.getDate() - 29);
    if (fromDate === toYMD(monthStart)) return "month";
    return null;
  };

  const dateFilterLabel = (): string => {
    const preset = getActiveDatePreset();
    if (preset === "today") return "Today";
    if (preset === "week") return "Last Week";
    if (preset === "month") return "Last Month";
    const f = activeTab === "mergePreview" ? mpFrom : from;
    const t = activeTab === "mergePreview" ? mpTo : to;
    if (f && t) return `${f} → ${t}`;
    if (f) return `From ${f}`;
    if (t) return `Until ${t}`;
    return "All dates";
  };

  const startItem = total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const endItem   = Math.min(page * itemsPerPage, total);

  const mpStartItem = mpTotal === 0 ? 0 : (mpPage - 1) * mpItemsPerPage + 1;
  const mpEndItem   = Math.min(mpPage * mpItemsPerPage, mpTotal);

  return (
    <div className="cdsl-page">

      {/* Tabs */}
      <div className="cdsl-header-row">
        <div className="cdsl-tabs">
        <button
          className={`cdsl-tab${activeTab === 'sessions' ? ' cdsl-tab--active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Session Logs
        </button>
        <button
          className={`cdsl-tab${activeTab === 'mergePreview' ? ' cdsl-tab--active' : ''}`}
          onClick={() => setActiveTab('mergePreview')}
        >
          AI Merge Preview
        </button>
        <button
          className={`cdsl-tab${activeTab === 'rules' ? ' cdsl-tab--active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          Rules
        </button>
      </div>
      <div style={{ marginLeft: 'auto', display: activeTab === 'rules' ? 'none' : 'flex', alignItems: 'center', gap: '8px' }}>
        <div ref={dateFilterMenuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className={`cdsl-date-filter-btn${getActiveDatePreset() || (activeTab === 'mergePreview' ? (mpFrom || mpTo) : (from || to)) ? " cdsl-date-filter-btn--active" : ""}`}
            title="Filter by date"
            aria-haspopup="true"
            aria-expanded={dateFilterOpen}
            onClick={() => setDateFilterOpen((p) => !p)}
          >
            <FiCalendar size={14} />
            <span className="cdsl-date-filter-label">
              {dateFilterLabel()}
            </span>
          </button>

          {dateFilterOpen && (
            <div className="cdsl-date-filter-popover" role="menu" aria-label="Filter by date">
              <div className="cdsl-filter-header">
                <span className="cdsl-filter-label">Filter by date</span>
                {(activeTab === 'mergePreview' ? (mpFrom || mpTo) : (from || to)) && (
                  <button
                    type="button"
                    className="cdsl-filter-clear"
                    onClick={() => {
                      if (activeTab === 'mergePreview') {
                        setMpFrom('');
                        setMpTo('');
                        setMpSearchFrom('');
                        setMpSearchTo('');
                        setMpPage(1);
                      } else {
                        setFrom('');
                        setTo('');
                        setSearchFrom('');
                        setSearchTo('');
                        setPage(1);
                      }
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>

              <div className="cdsl-preset-pills">
                {[
                  { key: "today", label: "Today" },
                  { key: "week", label: "Last Week" },
                  { key: "month", label: "Last Month" },
                ].map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`cdsl-preset-pill${getActiveDatePreset() === p.key ? " cdsl-preset-pill--active" : ""}`}
                    onClick={() => {
                      applyDatePreset(p.key as "today" | "week" | "month");
                      setDateFilterOpen(false);
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="cdsl-custom-dates">
                <div className="cdsl-date-row">
                  <label className="cdsl-date-row-label">From</label>
                  <input
                    type="date"
                    className="cdsl-date-row-input"
                    value={activeTab === 'mergePreview' ? mpFrom : from}
                    max={(activeTab === 'mergePreview' ? mpTo : to) || undefined}
                    onChange={e => handleFromChange(e.target.value)}
                  />
                </div>
                <div className="cdsl-date-row">
                  <label className="cdsl-date-row-label">To</label>
                  <input
                    type="date"
                    className="cdsl-date-row-input"
                    value={activeTab === 'mergePreview' ? mpTo : to}
                    min={(activeTab === 'mergePreview' ? mpFrom : from) || undefined}
                    onChange={e => handleToChange(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <button className="cdsl-refresh-btn" onClick={() => activeTab === 'mergePreview' ? void fetchMergePreview(mpSearchFrom, mpSearchTo) : fetchLogs(searchFrom, searchTo, page, itemsPerPage)} title="Refresh">
          <FiRefreshCw size={14} />
        </button>
      </div>
      </div>

      {activeTab === 'rules' ? (
        <CarrierDownloadRules />
      ) : activeTab === 'mergePreview' ? (
        <>

      {/* Table */}
      <div className="cdsl-card cdsl-card--table">
        <div className="cdsl-card-header cdsl-mp-filters">
          <div className="cdsl-mp-search">
            <FiSearch size={13} />
            <input
              value={mpQuery}
              onChange={e => setMpQuery(e.target.value)}
              placeholder="Search insured, policy #, carrier, reason…"
              aria-label="Search merge preview"
            />
          </div>
          {mpFilterControls.map(f => (
            <select
              key={f.label}
              className={`cdsl-mp-select${f.value ? ' cdsl-mp-select--active' : ''}`}
              value={f.value}
              onChange={e => f.set(e.target.value)}
              aria-label={`Filter by ${f.label}`}
              title={f.value || f.allLabel}
            >
              <option value="">{f.allLabel}</option>
              {f.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          {mpHasFilters && (
            <button type="button" className="cdsl-mp-clear" onClick={clearMpFilters}>
              <FiX size={12} /> Clear filters
            </button>
          )}
        </div>
        {!mpLoading && mpServerTotal > mpAll.length && (
          <div className="cdsl-mp-cap">
            Showing the first {mpAll.length.toLocaleString()} of {mpServerTotal.toLocaleString()} rows for this date range. Narrow the dates to filter and sort them all.
          </div>
        )}

        {mpLoading ? (
          <div className="cdsl-skeleton">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="cdsl-skeleton-row" />)}
          </div>
        ) : mpLogs.length === 0 ? (
          <div className="cdsl-empty">{mpAll.length > 0 && mpHasFilters ? 'No records match these filters.' : 'No records found for the selected date range.'}</div>
        ) : (
          <div className="cdsl-table-wrap">
            <table className="cdsl-table">
              <thead>
                <tr>
                  {MP_COLUMNS.map(([key, label]) => {
                    const active = mpSort.key === key;
                    return (
                      <th
                        key={key}
                        className={`cdsl-th cdsl-th--sortable${active ? ' cdsl-th--sorted' : ''}`}
                        aria-sort={active ? (mpSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      >
                        <button type="button" className="cdsl-th-sort" onClick={() => toggleMpSort(key)} title={`Sort by ${label}`}>
                          {label}
                          <span className="cdsl-th-sort-icon" aria-hidden="true">
                            {active && mpSort.dir === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {mpLogs.map((row, i) => (
                  <tr key={`${row.policy_number_ivans}-${i}`} className="cdsl-tr">
                    <td className="cdsl-td">
                      <div className="cdsl-insured-name"><LinkableCell value={row.insured_name_ivans} url={row.insured_url} /></div>
                      <div className="cdsl-insured-address" title={row.insured_address_ivans}>{row.insured_address_ivans}</div>
                    </td>
                    <td className="cdsl-td"><LinkableCell value={row.policy_number_ivans} url={row.policy_url} /></td>
                    <td className="cdsl-td"><LinkableCell value={row.carrier_name_ivans} url={row.carrier_url} /></td>
                    <td className="cdsl-td">{row.line_of_business}</td>
                    <td className="cdsl-td cdsl-td--date">
                      {formatMergeDate(row.policy_effective_date)} – {formatMergeDate(row.policy_expiration_date)}
                    </td>
                    <td className="cdsl-td">
                      {cleanTransactionString(row.transaction_string)}
                    </td>
                    <td className="cdsl-td">
                      {(() => {
                        const meta = mergeStatusMeta(row.merge_status);
                        return (
                          <span className={`cdsl-status cdsl-status--${meta.tone}`}>
                            {meta.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="cdsl-td">
                      {(() => {
                        const { Icon, tone } = actionCategoryMeta(row.action_category);
                        return (
                          <span
                            className={`cdsl-action-badge cdsl-action-badge--${tone}`}
                            title={row.action_category.replace(/_/g, ' ')}
                          >
                            <Icon size={14} />
                          </span>
                        );
                      })()}
                    </td>
                    <td className="cdsl-td cdsl-td--notes" title={row.planned_action_reason}>{row.planned_action_reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!mpLoading && mpTotal > 0 && (
          <div className="cdsl-pagination">
            <div className="cdsl-pagination-pages">
              <button className="cdsl-pg-btn" disabled={mpPage === 1} onClick={() => setMpPage(1)} title="First">⟨⟨</button>
              <button className="cdsl-pg-btn" disabled={mpPage === 1} onClick={() => setMpPage(p => p - 1)} title="Previous">
                <FiChevronLeft size={13} />
              </button>

              {buildPageNumbers(mpPage, mpTotalPages).map((p, i) =>
                p === '...'
                  ? <span key={`mp-dots-${i}`} className="cdsl-pg-dots">...</span>
                  : <button
                      key={p}
                      className={`cdsl-pg-btn${mpPage === p ? ' cdsl-pg-btn--active' : ''}`}
                      onClick={() => setMpPage(p)}
                    >{p}</button>
              )}

              <button className="cdsl-pg-btn" disabled={mpPage === mpTotalPages} onClick={() => setMpPage(p => p + 1)} title="Next">
                <FiChevronRight size={13} />
              </button>
              <button className="cdsl-pg-btn" disabled={mpPage === mpTotalPages} onClick={() => setMpPage(mpTotalPages)} title="Last">⟩⟩</button>
            </div>

            <div className="cdsl-pagination-meta">
              <select
                className="cdsl-per-page-select"
                value={mpItemsPerPage}
                onChange={e => handleMpItemsPerPageChange(Number(e.target.value))}
              >
                {ITEMS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="cdsl-pagination-info">items per page</span>
              <span className="cdsl-pagination-count">{mpStartItem} - {mpEndItem} of {mpTotal} items</span>
            </div>
          </div>
        )}
      </div>
        </>
      ) : (
        <>

      {/* Table */}
      <div className="cdsl-card cdsl-card--table">
        {!loading && logs.length === 0 ? (
          <div className="cdsl-empty">No records found for the selected date range.</div>
        ) : (
          <div className="cdsl-table-wrap">
            <table className="cdsl-table">
              <thead>
                <tr>
                  <th className="cdsl-th">Create Date ↓</th>
                  <th className="cdsl-th">Transaction Date</th>
                  <th className="cdsl-th">Type</th>
                  <th className="cdsl-th">XML Type</th>
                  <th className="cdsl-th">Provider</th>
                  <th className="cdsl-th">Status</th>
                  <th className="cdsl-th">Carriers NAICs</th>
                  <th className="cdsl-th"># Items</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: itemsPerPage }).map((_, i) => (
                    <tr key={`skel-${i}`} className="cdsl-tr">
                      <td className="cdsl-td"><span className="cdsl-skel-bar" style={{ width: 100 }} /></td>
                      <td className="cdsl-td"><span className="cdsl-skel-bar" style={{ width: 100 }} /></td>
                      <td className="cdsl-td"><span className="cdsl-skel-bar" style={{ width: 80 }} /></td>
                      <td className="cdsl-td"><span className="cdsl-skel-bar" style={{ width: 60 }} /></td>
                      <td className="cdsl-td"><span className="cdsl-skel-bar" style={{ width: 90 }} /></td>
                      <td className="cdsl-td"><span className="cdsl-skel-bar cdsl-skel-bar--pill" style={{ width: 76 }} /></td>
                      <td className="cdsl-td"><span className="cdsl-skel-bar" style={{ width: 70 }} /></td>
                      <td className="cdsl-td cdsl-td--center"><span className="cdsl-skel-bar" style={{ width: 24 }} /></td>
                    </tr>
                  ))
                ) : (
                  logs.map((row, i) => (
                    <tr
                      key={row.databaseId || i}
                      className={`cdsl-tr cdsl-tr--clickable${sessionPanel?.row.databaseId === row.databaseId ? ' cdsl-tr--active' : ''}`}
                      onClick={() => openSession(row)}
                    >
                      <td className="cdsl-td cdsl-td--date">{formatDateTime(row.createDate)}</td>
                      <td className="cdsl-td cdsl-td--date">{formatDateTime(row.transactionRequestDate)}</td>
                      <td className="cdsl-td">{row.type.replace(/_/g, ' ')}</td>
                      <td className="cdsl-td">{row.xmlType}</td>
                      <td className="cdsl-td">{row.provider}</td>
                      <td className="cdsl-td">
                        <span className={`cdsl-status cdsl-status--${row.consumed === 'Consumed' ? 'consumed' : row.consumed === 'Partially_Consumed' ? 'partial' : 'not-consumed'}`}>
                          {row.consumed.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="cdsl-td">{row.carriersNaics || '—'}</td>
                      <td className="cdsl-td cdsl-td--center">{row.number ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="cdsl-pagination">
            <div className="cdsl-pagination-pages">
              <button className="cdsl-pg-btn" disabled={page === 1} onClick={() => setPage(1)} title="First">⟨⟨</button>
              <button className="cdsl-pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">
                <FiChevronLeft size={13} />
              </button>

              {buildPageNumbers(page, totalPages).map((p, i) =>
                p === '...'
                  ? <span key={`dots-${i}`} className="cdsl-pg-dots">...</span>
                  : <button
                      key={p}
                      className={`cdsl-pg-btn${page === p ? ' cdsl-pg-btn--active' : ''}`}
                      onClick={() => setPage(p)}
                    >{p}</button>
              )}

              <button className="cdsl-pg-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">
                <FiChevronRight size={13} />
              </button>
              <button className="cdsl-pg-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">⟩⟩</button>
            </div>

            <div className="cdsl-pagination-meta">
              <select
                className="cdsl-per-page-select"
                value={itemsPerPage}
                onChange={e => handleItemsPerPageChange(Number(e.target.value))}
              >
                {ITEMS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="cdsl-pagination-info">items per page</span>
              <span className="cdsl-pagination-count">{startItem} - {endItem} of {total} items</span>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default CarrierDownloadSessionLogs;
